/**
 * [PORTABLE] React Query hooks for read-only Onboarding endpoints.
 * Target path in main project: src/features/onboarding/hooks/use-onboarding-queries.ts
 * Dependencies: @tanstack/react-query, @/store/use-auth-store
 */
import { useQuery } from "@tanstack/react-query";
import useAuthStore from "@/store/use-auth-store";

import { onboardingService } from "../api/onboarding.service";
import type { GenerationStatusResponse, Pillar } from "../types/onboarding.types";

export const ONBOARDING_QUERY_KEYS = {
  billingHistory: ["onboarding", "billingHistory"] as const,
  levels: (pillar?: Pillar) => ["onboarding", "levels", pillar ?? "all"] as const,
  progress: ["onboarding", "progress"] as const,
  generationStatus: (generationId: string | null) =>
    ["onboarding", "generation", generationId] as const,
};

export function useOnboardingLevels(pillar?: Pillar) {
  const accessToken = useAuthStore((s) => s.accessToken);
  return useQuery({
    queryKey: ONBOARDING_QUERY_KEYS.levels(pillar),
    queryFn: () => onboardingService.getLevels(pillar),
    enabled: !!accessToken,
    staleTime: Infinity,
  });
}

export function useOnboardingProgress() {
  const accessToken = useAuthStore((s) => s.accessToken);
  return useQuery({
    queryKey: ONBOARDING_QUERY_KEYS.progress,
    queryFn: onboardingService.getProgress,
    enabled: !!accessToken,
    staleTime: 30_000,
  });
}

export function useGenerationStatus(generationId: string | null, enabled = true) {
  const accessToken = useAuthStore((s) => s.accessToken);
  return useQuery<GenerationStatusResponse>({
    queryKey: ONBOARDING_QUERY_KEYS.generationStatus(generationId),
    queryFn: () => onboardingService.getGenerationStatus(generationId as string),
    enabled: !!accessToken && !!generationId && enabled,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status === "pending" || status === "processing" ? 2500 : false;
    },
    refetchIntervalInBackground: false,
  });
}

export function useOnboardingCredits() {
  const accessToken = useAuthStore((s) => s.accessToken);
  return useQuery({
    queryKey: ONBOARDING_QUERY_KEYS.billingHistory,
    queryFn: onboardingService.getBillingHistory,
    enabled: !!accessToken,
    // Kredinin 30 saniyede bir güncellenmesi veya taze kalması için
    staleTime: 30_000, 
  });
}